import type { DesignNode } from "./schema";
import svgpath from "svgpath";
export type VectorMatrix = [number, number, number, number, number, number];
type VectorSource = Pick<
  DesignNode,
  "width" | "height" | "pathWidth" | "pathHeight" | "pathViewport"
>;
export function vectorPathError(path: string): string | undefined {
  return (
    (svgpath(path) as ReturnType<typeof svgpath> & { err?: string }).err ||
    undefined
  );
}
export function normalizedVectorPath(
  node: VectorSource & Pick<DesignNode, "path">,
): string {
  const parsed = svgpath(node.path || "").matrix(vectorPathMatrix(node));
  const error = (parsed as typeof parsed & { err?: string }).err;
  if (error) throw new Error(error);
  return parsed.toString();
}
/** Maps source vector coordinates into the layer's top-left coordinate frame. */
export function vectorPathMatrix(node: VectorSource): VectorMatrix {
  const v = node.pathViewport;
  const sx = node.width / (v?.width ?? node.pathWidth ?? node.width);
  const sy = node.height / (v?.height ?? node.pathHeight ?? node.height);
  return v?.yAxis === "up"
    ? [sx, 0, 0, -sy, -v.x * sx, (v.y + v.height) * sy]
    : [sx, 0, 0, sy, -(v?.x ?? 0) * sx, -(v?.y ?? 0) * sy];
}
/** Maps the same vector coordinates directly into PDF page space. */
export function pdfVectorPathMatrix(
  node: DesignNode,
  pageHeight: number,
): VectorMatrix {
  const [sx, , , sy, dx, dy] = vectorPathMatrix(node);
  const angle = (node.rotation * Math.PI) / 180,
    c = Math.cos(angle),
    s = Math.sin(angle);
  return [
    c * sx,
    -s * sx,
    -s * sy,
    -c * sy,
    node.x +
      node.width / 2 +
      c * (dx - node.width / 2) -
      s * (dy - node.height / 2),
    pageHeight -
      (node.y +
        node.height / 2 +
        s * (dx - node.width / 2) +
        c * (dy - node.height / 2)),
  ];
}
